import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FileSearch, Search } from 'lucide-react';
import { complianceTasksAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';

const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (dateKey, days) => {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setDate(date.getDate() + days);
  return formatLocalDateKey(date);
};

const normalizeText = (value) => String(value ?? '').trim();
const normalizeKey = (value) => normalizeText(value).toLowerCase();

const resolveCount = (log = {}) => {
  const data = log?.data && typeof log.data === 'object' && !Array.isArray(log.data) ? log.data : {};
  const numeric = Number(data.count ?? data.quantity ?? data.totalQuantity);
  return Number.isFinite(numeric) ? numeric : null;
};

const DailyLiquorCountReport = () => {
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('reports.hub') && hasFeatureAccess('daily_liquor_count.report');
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const selectedStoreName = selectedStore?.name || selectedStore?.storeName || selectedStore?.store_code || selectedStore?.storeCode || 'Selected Store';
  const todayKey = formatLocalDateKey();

  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(shiftDate(todayKey, -7));
  const [toDate, setToDate] = useState(todayKey);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canViewPage) return;

    const loadLogs = async () => {
      if (!selectedStoreId) {
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const response = await complianceTasksAPI.getLogs({
          dateFrom: fromDate,
          dateTo: toDate,
          taskType: 'dailyLiquorCount',
          storeId: selectedStoreId,
        });

        const nextRows = (response.data?.data || [])
          .map((log) => ({
            id: log.id,
            date: log.task_date,
            storeId: String(log.store_id || ''),
            userName: log.users?.name || '-',
            sku: log.product_sku || '-',
            productName: log.product_name || '-',
            count: resolveCount(log),
            updatedAt: log.updated_at || log.completed_at || log.created_at || null,
          }))
          .filter((row) => row.count !== null)
          .sort((left, right) => {
            const dateCompare = String(right.date || '').localeCompare(String(left.date || ''));
            if (dateCompare !== 0) return dateCompare;
            return new Date(right.updatedAt || 0).getTime() - new Date(left.updatedAt || 0).getTime();
          });

        setRows(nextRows);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load daily liquor count report');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [canViewPage, fromDate, selectedStoreId, toDate]);

  const storeMap = useMemo(
    () => new Map(selectedStoreId ? [[String(selectedStoreId), selectedStoreName]] : []),
    [selectedStoreId, selectedStoreName]
  );

  const filteredRows = useMemo(() => {
    const query = normalizeKey(search);
    if (!query) return rows;

    return rows.filter((row) => [
      row.date,
      storeMap.get(row.storeId) || row.storeId,
      row.userName,
      row.sku,
      row.productName,
      row.count,
    ].some((value) => normalizeKey(value).includes(query)));
  }, [rows, search, storeMap]);

  const summary = useMemo(() => {
    const entryKeys = new Set(filteredRows.map((row) => `${row.date}::${row.storeId}::${row.sku}`));
    const totalCount = filteredRows.reduce((sum, row) => sum + Number(row.count || 0), 0);
    return {
      rows: filteredRows.length,
      uniqueEntries: entryKeys.size,
      totalCount,
    };
  }, [filteredRows]);

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Liquor Count Report</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Daily Liquor Count Report</h1>
        <p className="mt-1 text-sm text-gray-600">
          Review daily liquor count entries submitted by store teams.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card border border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">Rows</p>
          <p className="text-2xl font-bold text-slate-900">{summary.rows}</p>
        </div>
        <div className="card border border-blue-200 bg-blue-50">
          <p className="text-sm font-medium text-blue-800">Unique Entries</p>
          <p className="text-2xl font-bold text-blue-700">{summary.uniqueEntries}</p>
        </div>
        <div className="card border border-emerald-200 bg-emerald-50">
          <p className="text-sm font-medium text-emerald-800">Total Count</p>
          <p className="text-2xl font-bold text-emerald-700">{summary.totalCount}</p>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[240px] flex-[1_1_320px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              className="input pl-10"
              placeholder="Date, store, user, SKU, product..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="w-[150px]">
            <label className="label">Date From</label>
            <input
              type="date"
              className="input text-sm"
              value={fromDate}
              max={toDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>

          <div className="w-[150px]">
            <label className="label">Date To</label>
            <input
              type="date"
              className="input text-sm"
              value={toDate}
              min={fromDate}
              max={todayKey}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>

        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="table-scroll-shell">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Store</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">Product</th>
                <th className="px-4 py-3 font-semibold text-right">Count</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Loading daily liquor count report...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-gray-500">
                    <FileSearch className="mx-auto h-8 w-8 text-gray-300" />
                    <p className="mt-3">No daily liquor count entries found for the selected filters.</p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-gray-700">{row.date || '-'}</td>
                    <td className="px-4 py-3 text-gray-900">{storeMap.get(row.storeId) || row.storeId || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{row.userName}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{row.sku}</td>
                    <td className="px-4 py-3 text-gray-900">{row.productName}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{row.count}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyLiquorCountReport;
